/** The Contacts card (plan "Contacts card (D6)") — the person's emails, phones and URLs,
 *  through which the user's own email and phone are edited (there are no free-text email /
 *  phone fields). Between Trips and Reachability.
 *
 *  - The list is the `contacts` field: `ContactsView` in view mode, the records core's
 *    contacts widget in edit mode (its own errors, questions and "was …" per row). "Add
 *    contact" sits in the card header — in both modes the header keeps its height, so Edit
 *    moves nothing.
 *  - Freeze email / Freeze phone (editable) follow the list; Email verified is derived.
 *  - The account's own email / phone, each stored contact's OTA and dates (the kept
 *    `GET /users/<id>/contacts/`), the value actions and the owner link reach the widget
 *    through `ContactsProvider`.
 *  - View mode, a clean draft, an operator who may edit: ☆ Make user email / phone runs at
 *    once as a one-change changeset, after a confirmation that says it freezes the address
 *    (O10) — the record actions' "… now". */
import { Box } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';

import { useApplyUserChangeset, UserDriftError, type UserRecordApplyResult } from '@/api/userRecord';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { recordKey } from '@/features/records/draft/changeSet';
import { useDraftField, useDraftSummary } from '@/features/records/draft/DraftProvider';
import { asValidationFailure, failureMessage } from '@/features/records/editor/errors';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import type { RecordFieldGroup } from '@/features/records/panels/RecordPanel';
import {
  AddContactMenu,
  ContactsProvider,
  ContactsView,
  type ContactsContextValue,
  type ContactsLayout,
} from '@/features/records/fields/widgets/ContactsWidget';
import { asContactsValue, makeUserAddress, type ContactRow } from '@/features/records/fields/widgets/contactsModel';
import type { ViewFieldRef } from '@/features/records/views/types';

import { emailActions, phoneActions, urlActions } from '../../userLinks';
import type { UserDisplayContext } from '../cards/displays';
import { UserSectionPanel } from '../cards/UserSectionPanel';

import { useContactsMeta } from './useContactsMeta';

const LIST_KEY = 'contacts';

function groups(fields: ViewFieldRef[]): RecordFieldGroup[] {
  return [
    { id: 'list', fields: fields.filter((f) => f.key === LIST_KEY) },
    { id: 'freezes', fields: fields.filter((f) => f.key !== LIST_KEY) },
  ];
}

export function ContactsCard({
  section,
  display,
  layout,
}: {
  section: Parameters<typeof UserSectionPanel>[0]['section'];
  display: UserDisplayContext;
  /** The row layout, decided on the card's edit-mode width. */
  layout: ContactsLayout;
}) {
  const { detail: user, links } = display;
  const editor = useRecordEditor();
  const { mode, config, fieldByKey } = editor;
  const { count } = useDraftSummary();
  const rec = recordKey('user', user.id);
  const meta = useContactsMeta(user.id);
  const field = fieldByKey.get(LIST_KEY);
  const editable = field ? (config.editabilityOf?.(rec, field).editable ?? true) : false;
  const editing = mode === 'edit';
  const now = useMakeUserNow(user.id);

  const ctx = useMemo<ContactsContextValue>(
    () => ({
      account: {
        email: user.email || null,
        phone: user.phone,
        email_verified: user.email_verified,
        phone_verified: user.phone_verified,
      },
      ...(meta ? { meta } : {}),
      valueActions: (type, value) =>
        type === 'email' ? emailActions(value, links) : type === 'phone' ? phoneActions(value, links) : urlActions(value),
      layout,
      ...(!editing && editable && count === 0 ? { onMakeUserNow: now.ask } : {}),
    }),
    [user, meta, links, layout, editing, editable, count, now.ask],
  );

  const renderGroup = (g: RecordFieldGroup): ReactNode | undefined => {
    if (g.id !== 'list') return undefined;
    const f = g.fields[0];
    if (!f) return null;
    return <ContactListSlot id={f.id} />;
  };

  return (
    <ContactsProvider value={ctx}>
      <UserSectionPanel
        section={section}
        display={display}
        groupEditable={groups}
        renderGroup={renderGroup}
        meta={
          // The header keeps the button's height in both modes: Edit moves nothing.
          <Box sx={{ minHeight: 30, display: 'flex', alignItems: 'center' }}>
            {editing && editable && <AddContactMenu entity="user" id={user.id} path={LIST_KEY} />}
          </Box>
        }
      />
      {now.dialog}
    </ContactsProvider>
  );
}

/** The list's FieldSlot: the stored list as its view-mode display. */
function ContactListSlot({ id }: { id: number }) {
  const draft = useDraftField('user', id, LIST_KEY);
  return (
    <FieldSlot
      fieldKey={LIST_KEY}
      entity="user"
      id={id}
      path={LIST_KEY}
      label="Contact list"
      inlineChoices={false}
      display={<ContactsView rec={recordKey('user', id)} value={draft.baseline} />}
    />
  );
}

/** ☆ Make user email / phone now: a confirmation, then a changeset holding just that
 *  change (events suppressed, as every user change by default). */
function useMakeUserNow(userId: number): { ask: (row: ContactRow) => void; dialog: ReactNode } {
  const snackbar = useSnackbar();
  const [row, setRow] = useState<ContactRow | null>(null);
  const draft = useDraftField('user', userId, LIST_KEY);
  const apply = useApplyUserChangeset(userId, {
    onSaved: (r: UserRecordApplyResult) => {
      const word = row?.type === 'phone' ? 'User phone' : 'User email';
      snackbar.show(`${word} → ${row?.contact ?? ''} (frozen) · audit #${r.audit_id}`, 'success');
    },
  });
  const ask = useMemo(() => (r: ContactRow) => setRow(r), []);
  const confirm = () => {
    if (!row) return;
    const from = draft.baseline;
    const to = makeUserAddress(asContactsValue(from), row.key);
    apply.mutate(
      {
        events: { update_events: 'suppress' },
        changes: [{ entity: 'user', id: userId, field: LIST_KEY, from, to }],
      },
      {
        onError: (e) =>
          snackbar.show(
            asValidationFailure(e)
              ? `Not saved — ${failureMessage(e)}`
              : e instanceof UserDriftError
                ? 'Not saved — the contacts changed meanwhile; the page has the new ones'
                : failureMessage(e),
            'error',
          ),
        onSettled: () => setRow(null),
      },
    );
  };
  const word = row?.type === 'phone' ? 'user phone' : 'user email';
  const dialog = row ? (
    <ConfirmDialog
      open
      title={`Make ${row.contact} the ${word}?`}
      message={`Notifications go to it from now on, and it freezes the ${word}: booking systems no longer update it. Saved at once, with an audit entry.`}
      confirmLabel={`Make ${word}`}
      onConfirm={confirm}
      onCancel={() => setRow(null)}
    />
  ) : null;
  return { ask, dialog };
}
